import { matchPath } from 'react-router-dom';
import { adminRoutes, type AdminRouteConfig } from '@/router/routes';

export const ADMIN_VISITED_TABS_LIMIT = 12;

export interface AdminVisitedTab {
  id: string;
  href: string;
  pathname: string;
  label: string;
  group: string;
  closable: boolean;
  lastVisitedAt: number;
}

function matchedRoute(pathname: string): {
  route: AdminRouteConfig;
  params: Record<string, string | undefined>;
} | null {
  for (const route of adminRoutes) {
    const match = matchPath({ path: route.path, end: true }, pathname);
    if (match) return { route, params: match.params };
  }
  return null;
}

function shortIdentifier(value: string): string {
  return value.length > 8 ? `…${value.slice(-6)}` : value;
}

function tabLabel(
  route: AdminRouteConfig,
  params: Record<string, string | undefined>,
): string {
  const identifier = params.graphId ?? params.projectId ?? params.teamId;
  return identifier ? `${route.label} · ${shortIdentifier(identifier)}` : route.label;
}

export function createAdminVisitedTab(
  pathname: string,
  search = '',
  hash = '',
  lastVisitedAt = Date.now(),
): AdminVisitedTab | null {
  const matched = matchedRoute(pathname);
  if (!matched || matched.route.tabVisible === false) return null;

  return {
    id: pathname,
    href: `${pathname}${search}${hash}`,
    pathname,
    label: tabLabel(matched.route, matched.params),
    group: matched.route.group,
    closable: true,
    lastVisitedAt,
  };
}

export function findDefaultAdminVisitedTab(
  canAccess: (permission: string) => boolean,
): AdminVisitedTab | null {
  const defaultRoute = adminRoutes.find(route => (
    route.tabVisible !== false
    && !route.hidden
    && !route.path.includes(':')
    && canAccess(route.permission)
  ));
  if (!defaultRoute) return null;

  const tab = createAdminVisitedTab(defaultRoute.path);
  return tab ? { ...tab, closable: false } : null;
}

export function refreshAdminVisitedTab(
  tab: AdminVisitedTab,
  canAccess: (permission: string) => boolean,
): AdminVisitedTab | null {
  const matched = matchedRoute(tab.pathname);
  if (
    !matched
    || matched.route.tabVisible === false
    || !canAccess(matched.route.permission)
  ) {
    return null;
  }

  let href = tab.pathname;
  try {
    const parsed = new URL(tab.href, 'https://admin.wanjianshi.local');
    if (parsed.pathname === tab.pathname) {
      href = `${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
  } catch {
    // 损坏的本地缓存只恢复页面路径，不影响后台进入页面。
  }

  return {
    ...tab,
    id: tab.pathname,
    href,
    label: tabLabel(matched.route, matched.params),
    group: matched.route.group,
    closable: true,
  };
}

export function upsertAdminVisitedTab(
  tabs: AdminVisitedTab[],
  incoming: AdminVisitedTab,
): AdminVisitedTab[] {
  const existingIndex = tabs.findIndex(tab => tab.id === incoming.id);
  if (existingIndex >= 0) {
    return tabs.map((tab, index) => (
      index === existingIndex
        ? { ...incoming, closable: tab.closable }
        : tab
    ));
  }

  const next = [...tabs, incoming];
  if (next.length <= ADMIN_VISITED_TABS_LIMIT) return next;

  const removable = next
    .filter(tab => tab.closable && tab.id !== incoming.id)
    .sort((left, right) => left.lastVisitedAt - right.lastVisitedAt)[0];
  return removable ? next.filter(tab => tab.id !== removable.id) : next;
}
